import { createStyles, makeStyles } from '@hawkins/styles';
// TODO: Should these measurements be provided via the HawkinsTheme object?
/**
 * Width of a single instance of a date picker
 * See Figma for full measurements: https://www.figma.com/file/guNFUaRXVHGePsyJ5fToO6/Professional%3A-Light?node-id=1014%3A815
 */
export var DATEPICKER_SINGLE_WIDTH = 340;
/**
 * Height of a single calendar day cell
 * See Figma for full measurements: https://www.figma.com/file/guNFUaRXVHGePsyJ5fToO6/Professional%3A-Light?node-id=1014%3A815
 */
export var DATEPICKER_DAY_HEIGHT = 40;
/**
 * Width of the quick select menu.
 * See Figma for full measurements: https://www.figma.com/file/guNFUaRXVHGePsyJ5fToO6/Professional%3A-Light?node-id=1014%3A815
 */
export var DATEPICKER_QUICK_SELECT_WIDTH = 196;
export var datePickerStyles = function (theme) {
    return createStyles({
        /** Class applied to the calendar day that represents today */
        calendarDayToday: {
            '&:before': {
                border: "".concat(theme.hawkinsVariables.borderWidths.borderWidth1, " solid ").concat(theme.hawkins.ui.borderColorXlight),
                borderRadius: '50%',
                bottom: 0,
                content: '""',
                display: 'block',
                left: 2.5,
                right: 2.5,
                position: 'absolute',
                top: 0,
            },
            position: 'relative',
        },
        /** Class applied to each individual day in the calendar grid */
        calendarDay: {
            // HACK(style): CSS override specificity.
            '&.Hawkins-Button': { fontWeight: theme.hawkinsVariables.fontWeights.fontWeight0 },
            '&:hover': {
                '&$calendarDayToday': {
                    '&:before': {
                        borderColor: theme.hawkins.focus.borderColorRegular,
                    },
                },
                '&:not($calendarDayToday)': {
                    borderColor: theme.hawkins.focus.borderColorRegular,
                },
                background: 'none',
            },
            alignItems: 'center',
            borderWidth: 2,
            display: 'flex',
            height: DATEPICKER_DAY_HEIGHT,
            justifyContent: 'center',
            padding: 0,
        },
        /** Class applied to the day when the day is disabled */
        calendarDayDisabled: {
            color: theme.hawkins.textColor.textColorXlight,
            '&:hover': {
                '&:not($calendarDayToday)': {
                    borderColor: theme.hawkinsVariables.colors.transparent,
                },
            },
        },
        /** Class applied to a day when it is highlighted as part of a pre-determined range */
        calendarDayHighlighted: {
            backgroundColor: theme.hawkins.colors.grayT10,
            borderRadius: 0,
        },
        /** Class applied to a day when it is selected */
        calendarDaySelected: {
            backgroundColor: theme.hawkins.state.backgroundColorSelected,
            borderRadius: 0,
        },
        /**
         * Class applied to the first calendar day in a range, or the currently-selected calendar day
         * @private
         */
        calendarDayRangeFirstInstance: {
            borderBottomLeftRadius: theme.hawkinsVariables.borderRadius.borderRadius0,
            borderTopLeftRadius: theme.hawkinsVariables.borderRadius.borderRadius0,
        },
        /**
         * Class applied to the last calendar day in a range, or the currently-selected calendar day
         * @private
         */
        calendarDayRangeLastInstance: {
            borderBottomRightRadius: theme.hawkinsVariables.borderRadius.borderRadius0,
            borderTopRightRadius: theme.hawkinsVariables.borderRadius.borderRadius0,
        },
        /** Class applied to the actual calendar "grid" that displays the weeks and days */
        calendarBody: {
            display: 'grid',
            gridTemplateColumns: 'repeat(7, 1fr)',
        },
        /** Class applied to the wrapping element that fully wraps around each calendar instance (which includes month + year selectors) */
        calendarWrapper: {
            backgroundColor: theme.hawkins.ui.backgroundColor0,
            borderRadius: theme.hawkinsVariables.borderRadius.borderRadius0,
            display: 'inline-block',
            padding: theme.hawkinsVariables.spacings.space2,
        },
        /** Class applied to the element that wraps around the body of the calendar grid */
        calendarBodyWrapper: {
            position: 'relative',
        },
        /** Class applied to the change current month button */
        changeCurrentMonthBtn: {},
        /** Class applied to the change current year button */
        changeCurrentYearBtn: {},
        /** Class applied to the root of the change current month component */
        changeCurrentMonthOrYear: {
            backgroundColor: theme.hawkins.ui.backgroundColor0,
            bottom: 0,
            left: -theme.hawkinsVariables.spacingValues.space2,
            margin: 0,
            overflowY: 'auto',
            padding: 0,
            position: 'absolute',
            right: -theme.hawkinsVariables.spacingValues.space2,
            top: 0,
            zIndex: 1,
        },
        /** Class applied to the container <div /> that houses the date picker year, month, quick select and calendar bodies */
        datePickerAndQuickSelectContainer: {
            alignItems: 'flex-end',
            display: 'flex',
            gap: theme.hawkinsVariables.spacings.space1,
        },
        /** Class applied to the container that will render an optionally-provided footer node */
        footer: {
            flexGrow: 1,
            flexShrink: 0,
            width: '100%',
        },
        /** Class applied to the datepicker's heading that displays the current month and the previous and next buttons */
        /** Class applied to the datepicker's heading buttons that allow a user to change the month or year */
        headingBtn: {
            minHeight: 'initial',
            padding: 0,
        },
        /** Class applied to the button that goes to the next month */
        nextBtn: {
            position: 'absolute',
            right: 0,
            top: -theme.hawkinsVariables.spacingValues.space1,
        },
        /** Class applied to the button that goes to the previous month */
        previousBtn: {
            left: 0,
            position: 'absolute',
            top: -theme.hawkinsVariables.spacingValues.space1,
        },
        /** Class applied to each calendar's heading bar that contains the arrow buttons and month + year selectors */
        heading: {
            '& > $nextBtn, & > $previousBtn': {
                position: 'absolute', // we need specificity this to prevent the focus styling from changing the position
            },
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'center',
            marginBottom: theme.hawkinsVariables.spacings.space1,
            position: 'relative',
            width: DATEPICKER_SINGLE_WIDTH,
        },
        /** Class applied to each calendar's heading bar that contains the arrow buttons and month + year selectors when `pickerVariant === 'datetimepicker'` */
        headingWithTimePicker: {
            gridColumn: '1 / 3',
        },
        /** Class applied to the quick select menu */
        quickSelect: {
            alignItems: 'flex-start',
            backgroundColor: theme.hawkins.ui.backgroundColor0,
            bottom: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: theme.hawkinsVariables.spacings.space1,
            left: 0,
            justifyContent: 'stretch',
            padding: theme.hawkinsVariables.spacings.space3,
            position: 'absolute',
            top: 0,
            width: DATEPICKER_QUICK_SELECT_WIDTH,
        },
        /** Class applied to one of the buttons that represents a quick select preset */
        quickSelectPreset: {
            justifyContent: 'flex-start',
            textAlign: 'left',
            whiteSpace: 'normal',
            width: '100%',
        },
        /** Class applied to the TimePicker's root DOM element */
        timePicker: {
            paddingBottom: 0,
            paddingLeft: 0,
            paddingTop: 0,
        },
        /** Class applied to the <div /> that wraps around the TimePicker instance rendered when using `pickerVariant === 'datetimepicker'` */
        timePickerContainer: {
            padding: theme.hawkinsVariables.spacings.space2,
        },
        /** Class applied to the root container of the datepicker */
        root: {
            backgroundColor: theme.hawkins.ui.backgroundColor0,
            display: 'inline-block',
        },
        /** Class applied to the root when quickSelect is enabled */
        rootHasQuickSelect: {
            paddingLeft: DATEPICKER_QUICK_SELECT_WIDTH,
            position: 'relative',
        },
    });
};
export var useDatePickerStyles = makeStyles(datePickerStyles, { name: 'Hawkins-DatePicker' });
